/**
 * Minimap - Round, glassy radar HUD (Game UI Kit) showing player position,
 * cardinal direction labels, and tracked targets relative to the player.
 */

import * as THREE from 'three';

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface MinimapOptions extends BaseUIComponentOptions {
  /** Diameter of the minimap in pixels. */
  size?: number;
  /** World radius (in world units) covered by the minimap edge. */
  worldRadius?: number;
  /** Color of the player arrow at the center. */
  playerColor?: string;
  /** Default color used for target dots. */
  targetColor?: string;
  /** Color used to draw the cardinal direction labels. */
  cardinalColor?: string;
  /**
   * @deprecated Background is now driven by the minimap.css glass surface.
   * Retained for API compatibility; the value is ignored.
   */
  backgroundColor?: string;
  /**
   * @deprecated Border is now driven by the minimap.css glass surface.
   * Retained for API compatibility; the value is ignored.
   */
  borderColor?: string;
}

export interface MinimapTarget {
  position: THREE.Vector3;
  color?: string;
  size?: number;
}

export class Minimap extends BaseUIComponent<MinimapOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Minimap',
    category: 'navigation',
    summary: 'Radar-style HUD showing player position and tracked targets.',
    useCases: ['minimap', 'radar', 'map', 'mini-map', 'tracking', 'enemies on map'],
    optionsType: 'MinimapOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/minimap.html',
      styles: '@engine/assets/ui/styles/minimap.css',
    },
  };

  private rootElement: HTMLElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;

  private playerPosition: THREE.Vector3 = new THREE.Vector3();
  private targets: MinimapTarget[] = [];

  protected override getAssetPaths() {
    return {
      templatePath: Minimap.metadata.assetPaths.template,
      stylesPath: Minimap.metadata.assetPaths.styles,
    };
  }

  protected override getDefaultOptions(): Required<MinimapOptions> {
    return {
      position: 'top-right',
      visible: true,
      customClasses: [],
      customStyles: {},
      size: 200,
      worldRadius: 40,
      playerColor: '#4ade80',
      targetColor: '#ef4444',
      cardinalColor: 'rgba(232, 234, 237, 0.85)',
      backgroundColor: '',
      borderColor: '',
    };
  }

  protected override getInitialData(): Record<string, any> {
    return {
      size: this.options.size,
    };
  }

  protected override cacheElements(): void {
    if (!this.layout) return;
    this.rootElement = this.layout.querySelector('.ui-minimap') as HTMLElement | null;
    this.canvas = this.layout.querySelector('.ui-minimap-canvas') as HTMLCanvasElement | null;
    if (this.canvas) {
      this.ctx = this.canvas.getContext('2d');
      this.canvas.width = this.options.size;
      this.canvas.height = this.options.size;
    }
  }

  public setPlayerPosition(position: THREE.Vector3): void {
    this.playerPosition.copy(position);
  }

  public setTargets(targets: MinimapTarget[]): void {
    this.targets = [...targets];
  }

  public addTarget(target: MinimapTarget): void {
    this.targets.push(target);
  }

  public removeTarget(index: number): void {
    if (index >= 0 && index < this.targets.length) {
      this.targets.splice(index, 1);
    }
  }

  public clearTargets(): void {
    this.targets = [];
  }

  public render(): void {
    if (!this.canvas || !this.ctx) return;

    const ctx = this.ctx;
    const size = this.options.size;
    const center = size / 2;
    const radius = size / 2;

    // Transparent clear — the glassy backdrop comes from CSS.
    ctx.clearRect(0, 0, size, size);

    // Clip to the circular panel so target dots near the edge are cropped.
    ctx.save();
    ctx.beginPath();
    ctx.arc(center, center, radius, 0, 2 * Math.PI);
    ctx.clip();

    this.drawCardinalDirections(ctx, size);

    // Draw target dots positioned relative to the player.
    this.targets.forEach(target => {
      const relativeX = target.position.x - this.playerPosition.x;
      const relativeZ = target.position.z - this.playerPosition.z;

      // Z maps to Y on the minimap with negative Z pointing North/up.
      const mapX = center + (relativeX / this.options.worldRadius) * radius;
      const mapZ = center + (-relativeZ / this.options.worldRadius) * radius;

      const dx = mapX - center;
      const dz = mapZ - center;
      if (dx * dx + dz * dz <= radius * radius) {
        ctx.fillStyle = target.color ?? this.options.targetColor;
        ctx.beginPath();
        ctx.arc(mapX, mapZ, target.size ?? 3, 0, 2 * Math.PI);
        ctx.fill();
      }
    });

    // Player arrow pointing North at the center.
    this.drawPlayerArrow(ctx, center);

    ctx.restore();
  }

  private drawPlayerArrow(ctx: CanvasRenderingContext2D, center: number): void {
    const half = 6;
    ctx.fillStyle = this.options.playerColor;
    ctx.beginPath();
    ctx.moveTo(center, center - half);
    ctx.lineTo(center + half, center + half);
    ctx.lineTo(center, center + half * 0.5);
    ctx.lineTo(center - half, center + half);
    ctx.closePath();
    ctx.fill();
  }

  private drawCardinalDirections(ctx: CanvasRenderingContext2D, size: number): void {
    ctx.fillStyle = this.options.cardinalColor;
    ctx.font = '600 12px \'Inter\', sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const padding = 14;
    const center = size / 2;

    ctx.fillText('N', center, padding);
    ctx.fillText('S', center, size - padding);
    ctx.fillText('E', size - padding, center);
    ctx.fillText('W', padding, center);
  }

  public setSize(size: number): void {
    (this.options as any).size = size;
    if (this.rootElement) {
      this.rootElement.style.setProperty('--ui-minimap-size', `${size}px`);
    }
    if (this.canvas) {
      this.canvas.width = size;
      this.canvas.height = size;
    }
  }

  public setWorldRadius(radius: number): void {
    (this.options as any).worldRadius = radius;
  }

  protected override onDestroy(): void {
    this.rootElement = null;
    this.canvas = null;
    this.ctx = null;
    this.targets = [];
  }
}